// src/services/instagramDmLedgerService.ts
//
// Ch76 · client for GET /admin/instagram/dm-ledger
// (backend/api/routes/admin_instagram.py -> backend/api/services/instagram_dm_ledger.py).
//
// The DM path (webhook -> instagram_messages -> durable reply worker) is live,
// and until PR #474 nothing could READ its result. This service is the first
// screen consumer. Contract rules carried over from the backend: content-free
// by construction (hashed sender, 40-char preview), and every figure that
// could not be measured arrives as the literal "unknown" — never a 0 over a
// table that could not be read. Render the word.

import { fetchWithAuth } from "../lib/api";

export type Unknown = "unknown";

export interface DmReply {
  status: "sent" | "queued" | "failed" | "none" | Unknown;
  state?: string;
  sent_at?: string | null;
  terminal_at?: string | null;
  error_class?: string | null;
  attempts?: number | null;
}

export interface DmMessage {
  id: string;
  received_at: string | null;
  sender_hash: string;
  preview: string;
  reply: DmReply;
}

export interface DmHeartbeat {
  job_name: string;
  status: "ok" | "fail" | Unknown;
  last_ok_at?: string | null;
  last_error?: string | null;
  consecutive_misses?: number | Unknown;
  expected_interval_min?: number | Unknown;
  updated_at?: string | null;
  meta?: Record<string, unknown>;
  /** Ch76 identity probe (PR #477). Absent on an older backend → render unknown. */
  token_identity?: "ok" | "red" | Unknown;
  token_identity_checked_at?: string | null;
  token_identity_reason?: string;
}

export interface DmLedgerSummary {
  inbound_total: number | Unknown;
  inbound_24h: number | Unknown;
  replied_total: number | Unknown;
  replied_24h: number | Unknown;
  failed_24h: number | Unknown;
  last_inbound_at: string | null | Unknown;
  last_reply_at: string | null | Unknown;
}

export interface DmLedgerResponse {
  computed_at: string;
  limit: number;
  preview_chars: number;
  /** "unknown" when the messages table could not be read. */
  messages: DmMessage[] | Unknown;
  summary: DmLedgerSummary;
  heartbeats: Record<string, DmHeartbeat>;
}

export async function fetchInstagramDmLedger(limit = 5): Promise<DmLedgerResponse> {
  const res = await fetchWithAuth<DmLedgerResponse>(`/admin/instagram/dm-ledger?limit=${limit}`);
  // The service is the shape guard (same rule as adminTruthService).
  if (
    !res ||
    typeof res !== "object" ||
    typeof (res as any).summary !== "object" ||
    (res as any).summary === null ||
    typeof (res as any).heartbeats !== "object" ||
    (res as any).heartbeats === null
  ) {
    throw new Error("Malformed dm-ledger response: `summary`/`heartbeats` missing");
  }
  return res;
}
